import { trim } from "lodash"
import {
  ValidationError,
  TechnicalError,
  ForbiddenError,
  NotJsonError,
  UnauthorizedError,
  NotFoundError,
  TooManyRequestsError,
  ConflictError,
} from "./errors"

export type ProcessResponseResult = Promise<unknown> | string | undefined

function getCustomResponseHeaders(response: Response): Record<string, string> {
  return Object.fromEntries(
    [...response.headers].filter(([key]) => key.startsWith("x-omegapoint-")),
  )
}

// Returns "" when the header is absent/unparseable so the browser derives the filename from the
// URL (matches the old downloadCsv behavior). A literal fallback would strip the URL-derived
// name/extension from CSV exports when the server omits Content-Disposition.
function filenameFromContentDisposition(header: string | null): string {
  if (!header) {
    return ""
  }

  const quoted = header.match(/filename="([^"]+)"/i)
  if (quoted?.[1]) {
    return quoted[1]
  }

  return trim(header.replace(/^attachment;\s*filename=/i, ""), '"')
}

export async function downloadAttachment(response: Response): Promise<void> {
  if (response.status === 401) throw new UnauthorizedError()
  if (response.status === 403) throw new ForbiddenError()
  if (response.status === 429) throw new TooManyRequestsError(response.headers.get("Retry-After"))

  if (response.status === 404) {
    throw new NotFoundError(await response.text())
  }

  if (response.status < 200 || response.status >= 300) {
    throw new TechnicalError(await response.text())
  }

  const filename = filenameFromContentDisposition(response.headers.get("Content-Disposition"))
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)

  try {
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    // Download initiation is async; revoking synchronously here can race it and produce an
    // empty/failed file. Defer to the next tick so the click is processed first.
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }
}

function downloadCsv(response: Response): Promise<void> {
  return downloadAttachment(response)
}

function parseJsonBody(response: Response): Promise<unknown> {
  return response.json().then((body: { errors?: unknown, error?: unknown }) => {
    if (response.status === 400) {
      if (body.errors) throw new ValidationError(body.errors, getCustomResponseHeaders(response))
      throw new TechnicalError(body)
    }
    // 409 = optimistic-concurrency conflict (layout CAS). Preserve the status so the caller can
    // refetch + retry, rather than collapsing it into a generic TechnicalError.
    if (response.status === 409) throw new ConflictError(body.error || body)
    if (response.status > 400) throw new TechnicalError(body.error || body)
    return body
  })
}

export type TextContentResult =
  | { kind: "text", text: string }
  | { kind: "error", status: number }

export async function processTextResponse(response: Response): Promise<TextContentResult> {
  if (!response.ok) {
    return { kind: "error", status: response.status }
  }
  const text = await response.text()
  return { kind: "text", text }
}

function parseTextBody(response: Response): Promise<null> {
  return response.text().then((body) => {
    if (response.status === 500) throw new TechnicalError(body)
    if (!body) return null
    throw new NotJsonError(response)
  })
}

/**
 * Processes a Fetch API Response into a resolved value or throws a typed error.
 *
 * Routing order:
 *  1. 201 — JSON body if present, else the Location header (or undefined); 202/204 resolve without a body
 *  2. Synchronous error statuses (401, 403, 429)
 *  3. 404 — always reads the body as text, never as JSON
 *  4. Content-type dispatch for the remaining responses (CSV → download, JSON → parse, other → text fallback)
 */
export function processResponse(response: Response): ProcessResponseResult {
  // 201 may carry the created resource (POST /layouts), a Location header, or nothing — return the
  // JSON body when present so resource-returning creates aren't dropped, else keep the Location/undefined contract.
  if (response.status === 201) {
    const createdContentType = response.headers.get("content-type") ?? ""
    if (createdContentType.includes("application/json")) return parseJsonBody(response)
    return response.headers.get("location") ?? undefined
  }
  if (response.status === 202 || response.status === 204) return undefined

  if (response.status === 401) throw new UnauthorizedError()
  if (response.status === 403) throw new ForbiddenError()
  if (response.status === 429) throw new TooManyRequestsError(response.headers.get("Retry-After"))

  if (response.status === 404) {
    return response.text().then((body) => {
      throw new NotFoundError(body, getCustomResponseHeaders(response))
    })
  }

  const contentType = response.headers.get("content-type") ?? ""
  if (contentType.includes("text/csv")) return downloadCsv(response)
  if (contentType.includes("application/json")) return parseJsonBody(response)
  return parseTextBody(response)
}
